import React from 'react';
import { motion } from 'framer-motion';
import { getMasteryLabelInfo } from './masteryLabelInfo';
interface MasteryProgressBarProps {
  /** Beheersing 0-100. Wordt NIET als getal getoond — alleen als label en balk. */
  masteryScore: number;
  /** Tekst links van het label ("Voortgang"). Weglaten voor een compacte kaart. */
  caption?: string;
  /** Donkere kaart met witte tekst (LabStation) vs lichte kaart (kiezers). */
  tone?: 'light' | 'onColor';
}

/**
 * MasteryProgressBar — de enige beheersingsbalk in het leerlab.
 *
 * Deze balk stond vier keer bijna-identiek in de codebase: LabStation,
 * ModuleCard, FamilyCard en de fluency-modulekiezer. Drie van die vier misten
 * de toegankelijkheidsattributen die de vierde wél had, en twee toonden naast
 * het goedgekeurde label alsnog het ruwe percentage — precies wat het label moet
 * vervangen. Dat is het voorspelbare gevolg van vier kopieën: een correctie
 * landt op één plek en de andere drie drijven weg.
 *
 * Twee regels zitten daarom hier vast, niet bij de aanroeper:
 *
 * 1. GEEN PERCENTAGE. Een kind kan met "62%" niets en het nodigt uit tot
 *    vergelijken met een klasgenoot. Het beleid en de Nederlandse labels staan
 *    in `masteryLabelInfo.ts`.
 * 2. De balk is een `progressbar` met een `aria-valuetext` die hetzelfde label
 *    uitspreekt. Zonder valuetext leest een schermlezer het kale getal voor —
 *    dan is het percentage voor de één weggehaald en voor de ander niet.
 *
 * De vulanimatie is 400ms: het is een balk die zijn eindwaarde toont, geen
 * beloning. De oude 800-1000ms betekende dat een kind dat doorklikt de balk
 * halverwege achterlaat, en dan is een halfvolle balk de laatste indruk.
 */
export function MasteryProgressBar({
  masteryScore,
  caption,
  tone = 'light'
}: MasteryProgressBarProps) {
  const info = getMasteryLabelInfo(masteryScore);
  const clamped = Math.max(0, Math.min(100, masteryScore));
  const onColor = tone === 'onColor';
  return <div className="w-full" data-id="element-3217" data-mp="34bvhjuvr5wvg">
      <div className="flex items-center justify-between mb-1.5" data-id="element-3218" data-mp="17dlqfn8kzhey">
        {caption && <span className={`text-[10px] font-bold uppercase tracking-wider ${onColor ? 'text-white/80' : 'text-slate-400'}`} data-id="element-3219" data-mp="17bhj0vbh409x">
          
            {caption}
          </span>}
        <span className={`text-[10px] sm:text-xs font-bold ${onColor ? 'text-white' : info.color} ${caption ? '' : 'ml-auto'}`} data-id="element-3220" data-mp="1h3x2jx2f4wwx">
          
          {info.label}
        </span>
      </div>
      <div className={`h-2 w-full rounded-full overflow-hidden ${onColor ? 'bg-black/10' : 'bg-slate-100'}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamped)} aria-valuetext={info.label} aria-label={caption || 'Voortgang'} data-id="element-3221" data-mp="og0z89icg6r6">
        
        <motion.div className="h-full rounded-full" style={onColor ? undefined : {
        backgroundColor: info.ring
      }} initial={{
        width: 0
      }} animate={{
        width: `${clamped}%`
      }} transition={{
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-3222" data-mp="13crz0jvxjkhb">
          
          {onColor && <div className="h-full w-full bg-white rounded-full" data-id="element-3223" data-mp="330p9xe33yij" />}
        </motion.div>
      </div>
    </div>;
}